import React, { useMemo } from 'react';
import { calculateComprehensivePremium } from '../utils/comprehensiveRates';
import { formatKesDisplay, parseKesAmount } from '../utils/kesAmountFormat';
import './ComprehensiveBenefitsSection.css';

const MOTORCYCLE_CLASSES = new Set([
  'Motorcycle Private',
  'Motorcycle PSV',
  'Motorcycle PSV Annual',
  'Motorcycle PSV 6 Months'
]);

/**
 * Live COMP optional benefits + payable preview for Vehicle Information.
 */
export function useLiveComprehensiveQuote({
  enabled,
  vehicleClass,
  sumInsuredRaw,
  yearOfManufacture,
  benefits
}) {
  return useMemo(() => {
    if (!enabled) return { quote: null, error: null };
    const sumInsured = parseKesAmount(sumInsuredRaw);
    const yom = parseInt(yearOfManufacture, 10);
    if (!Number.isFinite(sumInsured) || sumInsured <= 0 || !Number.isFinite(yom)) {
      return { quote: null, error: null };
    }
    try {
      const quote = calculateComprehensivePremium({
        vehicleClass,
        sumInsured,
        yearOfManufacture: yom,
        benefits: MOTORCYCLE_CLASSES.has(vehicleClass)
          ? {}
          : {
              excessProtector: !!benefits.excessProtector,
              pvt: !!benefits.pvt,
              windscreen: benefits.windscreen || 0,
              radio: benefits.radio || 0
            }
      });
      return { quote, error: null };
    } catch (err) {
      return { quote: null, error: err.message, code: err.code };
    }
  }, [enabled, vehicleClass, sumInsuredRaw, yearOfManufacture, benefits]);
}

export function isMotorcycleCompClass(vehicleClass) {
  return MOTORCYCLE_CLASSES.has(vehicleClass);
}

export default function ComprehensiveBenefitsSection({
  vehicleClass,
  benefits,
  onChangeBenefits,
  quote,
  error
}) {
  const motorcycle = isMotorcycleCompClass(vehicleClass);

  return (
    <div className="comp-veh-benefits">
      <h3>Optional benefits</h3>
      {motorcycle ? (
        <p className="comp-veh-hint warn">
          Motorcycle Comprehensive is basic cover only — Excess Protector, PVT, Windscreen and Radio are
          not available.
        </p>
      ) : (
        <>
          <p className="comp-veh-hint">
            Excess Protector and PVT are rated as a % of sum insured. Click to include either or both.
            Enter windscreen/radio limits if needed. Payable updates instantly.
          </p>
          <div className="comp-veh-opt-cards">
            <button
              type="button"
              className={`comp-veh-opt-card ${benefits.excessProtector ? 'selected' : ''}`}
              onClick={() =>
                onChangeBenefits({ ...benefits, excessProtector: !benefits.excessProtector })
              }
            >
              <span className="title">Excess Protector</span>
              <span className="meta">% of sum insured · schedule minimum</span>
              <span className="state">{benefits.excessProtector ? 'Selected' : 'Click to include'}</span>
              {quote?.benefits?.excessProtector?.selected && (
                <span className="premium">+ {formatKesDisplay(quote.benefits.excessProtector.premium)}</span>
              )}
            </button>
            <button
              type="button"
              className={`comp-veh-opt-card ${benefits.pvt ? 'selected' : ''}`}
              onClick={() => onChangeBenefits({ ...benefits, pvt: !benefits.pvt })}
            >
              <span className="title">Political Violence & Terrorism</span>
              <span className="meta">% of sum insured · schedule minimum</span>
              <span className="state">{benefits.pvt ? 'Selected' : 'Click to include'}</span>
              {quote?.benefits?.pvt?.selected && (
                <span className="premium">+ {formatKesDisplay(quote.benefits.pvt.premium)}</span>
              )}
            </button>
          </div>
          <div className="comp-veh-limits">
            <div className="form-group">
              <label htmlFor="compWindscreen">Windscreen limit (KES)</label>
              <input
                id="compWindscreen"
                type="text"
                inputMode="numeric"
                placeholder="e.g. 50,000"
                value={benefits.windscreenDisplay || ''}
                onChange={(e) => {
                  const raw = e.target.value.replace(/[^\d,]/g, '');
                  const digits = raw.replace(/,/g, '');
                  onChangeBenefits({
                    ...benefits,
                    windscreen: digits ? Number(digits) : 0,
                    windscreenDisplay: digits ? Number(digits).toLocaleString('en-KE') : ''
                  });
                }}
              />
              <small>10% of amount above free limit</small>
            </div>
            <div className="form-group">
              <label htmlFor="compRadio">Radio limit (KES)</label>
              <input
                id="compRadio"
                type="text"
                inputMode="numeric"
                placeholder="e.g. 40,000"
                value={benefits.radioDisplay || ''}
                onChange={(e) => {
                  const digits = e.target.value.replace(/[^\d]/g, '');
                  onChangeBenefits({
                    ...benefits,
                    radio: digits ? Number(digits) : 0,
                    radioDisplay: digits ? Number(digits).toLocaleString('en-KE') : ''
                  });
                }}
              />
              <small>10% of amount above free limit</small>
            </div>
          </div>
        </>
      )}

      <aside className="comp-veh-summary" aria-live="polite">
        <h4>Amount to pay</h4>
        {error && <p className="comp-veh-error">{error}</p>}
        {!error && quote && (
          <ul>
            <li>
              <span>Basic</span>
              <strong>{formatKesDisplay(quote.basic.premium)}</strong>
            </li>
            {quote.benefits.excessProtector.premium > 0 && (
              <li>
                <span>Excess Protector</span>
                <strong>{formatKesDisplay(quote.benefits.excessProtector.premium)}</strong>
              </li>
            )}
            {quote.benefits.pvt.premium > 0 && (
              <li>
                <span>PVT</span>
                <strong>{formatKesDisplay(quote.benefits.pvt.premium)}</strong>
              </li>
            )}
            {quote.benefits.windscreen.value > 0 && (
              <li>
                <span>
                  Windscreen
                  {quote.benefits.windscreen.withinFreeLimit
                    ? ` (limit ${formatKesDisplay(quote.benefits.windscreen.value)} · within free)`
                    : ` (limit ${formatKesDisplay(quote.benefits.windscreen.value)})`}
                </span>
                <strong>
                  {quote.benefits.windscreen.withinFreeLimit
                    ? 'Included free'
                    : formatKesDisplay(quote.benefits.windscreen.premium)}
                </strong>
              </li>
            )}
            {quote.benefits.radio.value > 0 && (
              <li>
                <span>
                  Radio
                  {quote.benefits.radio.withinFreeLimit
                    ? ` (limit ${formatKesDisplay(quote.benefits.radio.value)} · within free)`
                    : ` (limit ${formatKesDisplay(quote.benefits.radio.value)})`}
                </span>
                <strong>
                  {quote.benefits.radio.withinFreeLimit
                    ? 'Included free'
                    : formatKesDisplay(quote.benefits.radio.premium)}
                </strong>
              </li>
            )}
            <li className="annual">
              <span>Annual total</span>
              <strong>{formatKesDisplay(quote.annualTotal)}</strong>
            </li>
            <li className="payable">
              <span>Pay now (30% · 1 month)</span>
              <strong>{formatKesDisplay(quote.payableAmount)}</strong>
            </li>
          </ul>
        )}
        {!error && !quote && (
          <p className="comp-veh-hint">Enter sum insured and year of manufacture to see the premium.</p>
        )}
      </aside>
    </div>
  );
}
